import { TFunction } from "i18next";

export const SINGLE_PROGRAM_EXTENSIONS = ["pg1", "pg2", "pg21", "pg22"];

export const CC4_BUNDLE_PARTS = ["cc4", "med", "inf"];

export const EMPTY_ALLOWED_BUNDLE_PARTS = ["med", "inf"];

export const allowsEmptyContent = (extension: string): boolean =>
  EMPTY_ALLOWED_BUNDLE_PARTS.includes(extension.toLowerCase());

export const ALL_PROGRAM_EXTENSIONS = [
  ...SINGLE_PROGRAM_EXTENSIONS,
  ...CC4_BUNDLE_PARTS,
];

export const PROGRAM_FILE_ACCEPT = ALL_PROGRAM_EXTENSIONS.map(
  (extension) => `.${extension}`,
).join(",");

export const KNITTING_PROGRAM_TYPES = [...SINGLE_PROGRAM_EXTENSIONS, "cc4"];

export const getFileExtension = (fileName: string): string => {
  const dotIndex = fileName.lastIndexOf(".");
  return dotIndex > 0 ? fileName.substring(dotIndex + 1).toLowerCase() : "";
};

export const getFileBaseName = (fileName: string): string => {
  const dotIndex = fileName.lastIndexOf(".");
  return dotIndex > 0 ? fileName.substring(0, dotIndex).toLowerCase() : "";
};

export const isCc4BundlePart = (extension: string): boolean =>
  CC4_BUNDLE_PARTS.includes(extension.toLowerCase());

export type MachineGeneration = "Cc4" | "Cc4II";

export const MACHINE_GENERATIONS: MachineGeneration[] = ["Cc4", "Cc4II"];

export const resolveMachineGeneration = (
  extensions: Iterable<string>,
): MachineGeneration | "" => {
  const parts = [...extensions].map((extension) => extension.toLowerCase());
  if (!parts.length) return "";

  if (parts.every(isCc4BundlePart)) return "Cc4";
  if (parts.every((part) => SINGLE_PROGRAM_EXTENSIONS.includes(part)))
    return "Cc4II";

  return "";
};

export const localizeMachineGeneration = (
  t: TFunction,
  machineGeneration: string,
): string => t(`KnittingMachine_Generation_${machineGeneration}`);

export const getMissingBundleParts = (
  extensions: Iterable<string>,
): string[] => {
  const present = new Set([...extensions].map((e) => e.toLowerCase()));
  return CC4_BUNDLE_PARTS.filter((part) => !present.has(part));
};

export const isCompleteBundle = (extensions: Iterable<string>): boolean => {
  const parts = [...extensions].map((e) => e.toLowerCase());
  return (
    parts.length === CC4_BUNDLE_PARTS.length &&
    getMissingBundleParts(parts).length === 0
  );
};

export const localizeKnittingProgramType = (
  t: TFunction,
  programType: string,
): string => {
  if (!programType) return "";

  const key = `KnittingProgram_ProgramType_${programType.toLowerCase()}`;
  const translated = t(key);
  return translated === key ? programType.toUpperCase() : translated;
};
